import { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { post } from '@network/api';
import { toast } from 'react-hot-toast';

import InputComponent from '@components/Input';
import InputPhoneNumber from '@components/InputPhoneNumber';
import Spinner from '@components/Spinner';
import BusinessName from '../BusinessName';
import { DataDetailCheckout, SettingsProps } from '@pages/types';
import { useCheckoutStep } from '@pages/hooks/useCheckoutStep';

interface FormUserProps {
  data: DataDetailCheckout;
  settings: SettingsProps;
  onRefresh?: () => void;
}

const FormUser: React.FC<FormUserProps> = ({ data, settings, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const { goToOverview } = useCheckoutStep();

  const handleOnSubmit = async (
    values: {
      name: string;
      email: string;
      phone_number: string;
      address: string;
    },
    invoice_id: string
  ) => {
    const formattedPhoneNumber =
      values.phone_number === '+62'
        ? ''
        : values.phone_number.replace(/^\+/, '');
    const payload = {
      name: values.name,
      email: values.email,
      phone_number: formattedPhoneNumber,
      address: values.address,
    };

    setLoading(true);
    await post(`/checkout/v1/customer?invoice_number=${invoice_id}`, payload)
      .then(async () => {
        if (onRefresh) {
          onRefresh();
        }

        goToOverview();
        setLoading(false);
      })
      .catch((err) => {
        toast.error(err.detail);
        setLoading(false);
      });
  };

  return (
    <div className="mb-5 flex flex-col gap-4 px-3 pt-8 md:mb-0 md:pb-4 lg:px-0">
      <BusinessName
        data={data}
        title="Invoice Payment"
        subtitle={`Please fill in your details to complete payment to ${data?.business?.name || ''}`}
      />
      <Formik
        initialValues={{
          name: data?.customer?.name || '',
          email: data?.customer?.email || '',
          phone_number: data?.customer?.phone || '',
          address: data?.customer?.address || '',
        }}
        validationSchema={Yup.object().shape({
          name: Yup.string()
            .min(4, 'Name min 8 characters')
            .max(20, 'Name max 20 characters')
            .required('Please enter your name'),
          phone_number: Yup.string()
            .test(
              'valid-phone-number',
              'Phone number must be valid and follow Indonesian format',
              (value) => {
                if (value === '+62') return true;
                return /^(?:\+62|62|0)[2-9][0-9]{7,12}$/.test(value || '');
              }
            )
            .nullable(),
        })}
        onSubmit={async (values) => {
          handleOnSubmit(values, data?.invoice_number);
        }}
        enableReinitialize={true}
      >
        {({ handleSubmit }) => (
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            <div className="grid grid-cols-12 gap-x-4 gap-y-6">
              <div className="col-span-full">
                <InputComponent name="name" type="text" label="Name" required />
              </div>
              <div className="col-span-full">
                <InputComponent name="email" type="email" label="Email" />
              </div>
              <div className="col-span-full">
                <InputPhoneNumber name="phone_number" label="Phone Number" />
              </div>
              <div className="col-span-full">
                <InputComponent name="address" type="text" label="Address" />
              </div>
            </div>

            <div className="fixed bottom-1 left-0 right-0 z-20 bg-white px-4 py-3 md:relative md:px-0">
              <button
                type="submit"
                disabled={loading}
                style={{
                  backgroundColor: settings.button_color
                    ? settings.button_color
                    : '#5D87FF',
                  color: settings.button_text_color
                    ? settings.button_text_color
                    : '#FFFFFF',
                }}
                className={`${
                  loading ? '!bg-gray-200 hover:!bg-gray-400' : ''
                } flex w-full flex-row items-center justify-center gap-2 rounded-md border border-transparent px-4 py-3 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-dark focus:ring-offset-2 md:mt-8 md:w-full md:rounded-md`}
              >
                {loading ? <Spinner /> : <span>CONTINUE</span>}
              </button>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default FormUser;
